/**
 * Display helpers for arrival-window presets. Times are `HH:mm` wall-clock
 * strings in the tenant zone — no timezone math belongs here.
 */
import {
  DAY_ORDER,
  DAY_SHORT,
  formatTime12,
} from '@/components/organisms/settings-scheduling/schedule-summary';

/** Weekday numbers in display order, Monday first. Backend numbering: 0=Sun..6. */
export const WEEKDAYS_MON_FIRST = [1, 2, 3, 4, 5, 6, 0] as const;

/** Backend weekday number → "Mon".."Sun", sharing the labels the Scheduling page uses. */
export const WEEKDAY_SHORT: Record<number, string> = Object.fromEntries(
  WEEKDAYS_MON_FIRST.map((day, index) => [day, DAY_SHORT[DAY_ORDER[index]]]),
);

/** "08:00" → 480. NaN-safe: an unparseable string reads as -1 so comparisons fail. */
export function timeToMinutes(value: string): number {
  const [h, m] = value.split(':').map((part) => Number(part));
  if (!Number.isInteger(h) || !Number.isInteger(m)) return -1;
  return h * 60 + m;
}

/**
 * "08:00" + "10:00" → "8:00–10:00 AM"; "11:00" + "13:00" → "11:00 AM–1:00 PM".
 * The period is written once when both ends share it, which is how a
 * dispatcher says it out loud.
 */
export function formatWindowRange(startTime: string, endTime: string): string {
  const start = formatTime12(startTime);
  const end = formatTime12(endTime);
  const startPeriod = start.slice(-2);
  const endPeriod = end.slice(-2);
  if (startPeriod === endPeriod) {
    return `${start.slice(0, -3)}–${end}`;
  }
  return `${start}–${end}`;
}

// "8:00–10:00 AM", "8-10am", "8 am to 10 am": a name that is only its hours.
const HOURS_ONLY_NAME =
  /^\s*\d{1,2}(:\d{2})?\s*([ap]\.?m\.?)?\s*(-|–|—|to)\s*\d{1,2}(:\d{2})?\s*([ap]\.?m\.?)?\s*$/i;

/**
 * A preset as a choice: "Morning · 8:00–10:00 AM". A window named after its
 * own hours adds nothing to them, so it reads as the hours alone instead of
 * "8:00–10:00 AM · 8:00–10:00 AM".
 */
export function formatPresetOption(preset: {
  label?: string | null;
  startTime: string;
  endTime: string;
}): string {
  const hours = formatWindowRange(preset.startTime, preset.endTime);
  const name = preset.label?.trim();
  return name && !HOURS_ONLY_NAME.test(name) ? `${name} · ${hours}` : hours;
}

/** "6 visits · 12 h", "6 visits", "12 h", or "—". */
export function formatPresetLimit(
  maxVisits: number | null | undefined,
  maxHours: number | string | null | undefined,
): string {
  const parts: string[] = [];
  if (maxVisits != null) {
    parts.push(`${maxVisits} ${maxVisits === 1 ? 'visit' : 'visits'}`);
  }
  const hours = maxHours == null ? null : Number(maxHours);
  if (hours != null && Number.isFinite(hours)) {
    parts.push(`${hours} h`);
  }
  return parts.length > 0 ? parts.join(' · ') : '—';
}

/** "Every day", "Mon–Fri", "Mon, Wed, Fri" — the long form for tooltips and the editor. */
export function describeWeekdays(weekdays: readonly number[]): string {
  if (weekdays.length === 0 || weekdays.length === 7) return 'Every day';
  const ordered = WEEKDAYS_MON_FIRST.filter((day) => weekdays.includes(day));
  // Collapse a single contiguous run (Mon–Fri); anything else is listed.
  const positions = ordered.map((day) => WEEKDAYS_MON_FIRST.indexOf(day));
  const contiguous = positions.every((pos, i) => i === 0 || pos === positions[i - 1] + 1);
  if (contiguous && ordered.length > 2) {
    return `${WEEKDAY_SHORT[ordered[0]]}–${WEEKDAY_SHORT[ordered[ordered.length - 1]]}`;
  }
  return ordered.map((day) => WEEKDAY_SHORT[day]).join(', ');
}
